import { computed, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { NotificacionPushPayload, SocketConnectOptions } from '../models/global.model';

@Injectable({ providedIn: 'root' })
export class NotificacionPushService implements OnDestroy {
  private socket?: Socket;
  private currentRoom?: string;
  private readonly notificacionesSubject = new BehaviorSubject<NotificacionPushPayload[]>([]);
  readonly notificaciones$ = this.notificacionesSubject.asObservable();
  readonly notificaciones = toSignal(this.notificaciones$, { initialValue: [] });
  readonly cantidadNotificaciones = computed(
    () => this.notificaciones().filter((notificacion) => !notificacion.read).length,
  );

  constructor(private http: HttpClient) {}

  connect(options: SocketConnectOptions): void {
    const room = this.getRoom(options);

    if (!room) {
      return;
    }

    if (this.socket?.connected && this.currentRoom === room) {
      return;
    }

    this.disconnect();
    this.currentRoom = room;
    void this.cargarHistorial(room);

    this.socket = io(environment.NOTIFICACIONES_PUSH_SOCKET_URL, {
      auth: this.getAuthPayload(options, room),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    this.socket.on('notificacion_push', (notificacion: NotificacionPushPayload) => {
      this.mergeNotificaciones([notificacion]);
    });

    this.socket.on('notificaciones_push_historial', (notificaciones: NotificacionPushPayload[]) => {
      this.mergeNotificaciones(notificaciones);
    });

    this.socket.on('notificaciones_push_limpiadas', () => {
      this.notificacionesSubject.next([]);
    });
  }

  public disconnect(): void {
    if (!this.socket) {
      return;
    }

    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = undefined;
  }

  async limpiarNotificaciones(): Promise<void> {
    const room = this.currentRoom;
    this.notificacionesSubject.next([]);

    if (!room) {
      return;
    }

    try {
      await lastValueFrom(
        this.http.delete(`${this.getHttpBaseUrl()}/notificaciones/${room}`, {
          headers: { 'Skip-Interceptor': 'true' },
        }),
      );
    } catch (error) {
      console.error('Error al limpiar notificaciones push:', error);
    }
  }

  async marcarComoLeida(notificacion: NotificacionPushPayload): Promise<void> {
    const notificationId = this.getNotificationId(notificacion);

    this.notificacionesSubject.next(
      this.notificacionesSubject.value.map((item) =>
        this.getNotificationId(item) === notificationId ? { ...item, read: true } : item,
      ),
    );

    if (!this.currentRoom || !notificacion.cacheKey) {
      return;
    }

    try {
      await lastValueFrom(
        this.http.patch(
          `${this.getHttpBaseUrl()}/notificaciones/${this.currentRoom}/leida`,
          { cacheKey: notificacion.cacheKey },
          { headers: { 'Skip-Interceptor': 'true' } },
        ),
      );
    } catch (error) {
      console.error('Error al marcar notificación push como leída:', error);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private async cargarHistorial(room: string): Promise<void> {
    try {
      const notificaciones = await lastValueFrom(
        this.http.get<NotificacionPushPayload[]>(`${this.getHttpBaseUrl()}/obtener-notificaciones/${room}`, {
          headers: { 'Skip-Interceptor': 'true' },
        }),
      );
      this.mergeNotificaciones(notificaciones);
    } catch (error) {
      console.warn('Error al obtener historial de notificaciones push:', error);
    }
  }

  private mergeNotificaciones(notificaciones: NotificacionPushPayload[]): void {
    const currentById = new Map(
      this.notificacionesSubject.value.map((notificacion) => [
        this.getNotificationId(notificacion),
        notificacion,
      ]),
    );

    for (const notificacion of notificaciones) {
      const id = this.getNotificationId(notificacion);
      const current = currentById.get(id);

      currentById.set(id, {
        ...current,
        ...notificacion,
        id,
        read: notificacion.read ?? current?.read ?? false,
      });
    }

    this.notificacionesSubject.next(
      [...currentById.values()].sort((a, b) => this.getTime(b) - this.getTime(a)),
    );
  }

  private getHttpBaseUrl(): string {
    return environment.NOTIFICACIONES_PUSH_SOCKET_URL.replace(/\/notificaciones-push\/?$/, '');
  }

  private getNotificationId(notificacion: NotificacionPushPayload): string {
    return (
      notificacion.cacheKey ??
      notificacion.id ??
      notificacion.jobId ??
      [notificacion.title, notificacion.body, notificacion.sentAt].filter(Boolean).join(':')
    );
  }

  private getTime(notificacion: NotificacionPushPayload): number {
    const fromDate = notificacion.sentAt ? new Date(notificacion.sentAt).getTime() : 0;

    if (Number.isFinite(fromDate) && fromDate > 0) {
      return fromDate;
    }

    const keyParts = notificacion.cacheKey?.split(':') ?? [];
    const fromKey = Number(keyParts[keyParts.length - 1]);
    return Number.isFinite(fromKey) ? fromKey : 0;
  }

  private getRoom(options: SocketConnectOptions): string | undefined {
    const room = options.usuarioId ?? options.cuentaId;
    return room?.toString();
  }

  private getAuthPayload(options: SocketConnectOptions, room: string): SocketConnectOptions {
    return options.cuentaId ? { cuentaId: room } : { usuarioId: room };
  }
}
